import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { formatTagKey, getNextEligibleFilters, parseTags, questionMatchesSteps } from "./TagFilterUtils";

function Checkbox({ checked, onChange, color = "#3f88a5", disabled = false }) {
  return (
    <button
      type="button"
      onClick={e => { e.stopPropagation(); if (!disabled) onChange(!checked); }}
      style={{
        width: 17, height: 17, borderRadius: 4, flexShrink: 0,
        border: `1.5px solid ${checked ? (disabled ? "#94a3b8" : color) : "#cbd5e1"}`,
        background: checked ? (disabled ? "#94a3b8" : color) : "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: disabled ? "not-allowed" : "pointer", padding: 0, outline: "none",
        fontSize: 10, fontWeight: 900, color: "#fff",
        transition: "all 0.15s ease",
        boxShadow: checked && !disabled ? `0 1px 3px ${color}44` : "none",
        opacity: disabled ? 0.7 : 1
      }}
    >
      {checked ? "✓" : null}
    </button>
  );
}

export default function TagFilterBar({
  allQuestions, filterSteps, onFilterStepsChange,
  pendingIds, disabled,
  savedIds = new Set(), onSelectFiltered,
  disableAllSelected = false,
  children // To allow injecting module-specific bars like FilterDbAssignBar
}) {
  const [openKey, setOpenKey] = useState(null);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpenKey(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => { setDropdownSearch(""); }, [openKey]);

  const activeSteps = filterSteps.filter(s => s.values?.size);

  const eligibleNextFilters = useMemo(
    () => getNextEligibleFilters(allQuestions, filterSteps, savedIds),
    [allQuestions, filterSteps, savedIds]
  );
  const nextKeys = useMemo(() => {
    const activeKeys = new Set(activeSteps.map(s => s.key));
    return eligibleNextFilters.filter(k => !activeKeys.has(k.key)).map(k => k.key);
  }, [eligibleNextFilters, activeSteps]);

  const openValues = useMemo(() => {
    if (!openKey) return [];
    const found = eligibleNextFilters.find(e => e.key === openKey);
    return found ? found.values : [];
  }, [openKey, eligibleNextFilters]);

  const visibleValues = useMemo(() => {
    if (!dropdownSearch.trim()) return openValues;
    const q = dropdownSearch.toLowerCase();
    return openValues.filter(v => v.toLowerCase().includes(q));
  }, [openValues, dropdownSearch]);

  const openSelectedVals = useMemo(() => {
    const s = filterSteps.find(s => s.key === openKey);
    return s?.values ?? new Set();
  }, [filterSteps, openKey]);

  const openIsLevelOne = useMemo(() => {
    if (!openKey) return false;
    const stepIdx = activeSteps.findIndex(s => s.key === openKey);
    return stepIdx === 0 || (stepIdx === -1 && activeSteps.length === 0);
  }, [openKey, activeSteps]);

  const getValueCounts = useCallback((key, value) => {
    const stepIdx = activeSteps.findIndex(s => s.key === key);
    const stepsPrefix = stepIdx >= 0 ? activeSteps.slice(0, stepIdx) : activeSteps;
    const steps = [...stepsPrefix, { key, values: new Set([value]) }];
    const matching = allQuestions.filter(q => questionMatchesSteps(q, steps));
    return {
      total: matching.length,
      selected: matching.filter(q => pendingIds?.has(q.questionId ?? q.kpiId)).length,
      saved: matching.filter(q => savedIds?.has(q.questionId ?? q.kpiId)).length,
    };
  }, [allQuestions, activeSteps, pendingIds, savedIds]);

  const toggleValue = (key, value) => {
    if (disabled) return;
    if (openIsLevelOne) {
      const alreadySelected = openSelectedVals.has(value) && openSelectedVals.size === 1;
      const newSteps = alreadySelected ? [] : [{ key, values: new Set([value]) }];
      onFilterStepsChange(newSteps);
      setOpenKey(null);
      if (onSelectFiltered) {
        onSelectFiltered(newSteps, {
          added: alreadySelected ? [] : [{ key, value }],
          removed: alreadySelected ? [{ key, value }] : []
        });
      }
      return;
    }
    const next = filterSteps.map(s => ({ ...s, values: new Set(s.values) }));
    const idx = next.findIndex(s => s.key === key);
    const added = [];
    const removed = [];
    if (idx === -1) {
      next.push({ key, values: new Set([value]) });
      added.push({ key, value });
    } else {
      const vals = next[idx].values;
      if (vals.has(value)) {
        vals.delete(value);
        removed.push({ key, value });
      } else {
        vals.add(value);
        added.push({ key, value });
      }
      if (!vals.size) { next.splice(idx, next.length - idx); setOpenKey(null); }
    }
    onFilterStepsChange(next);
    if (onSelectFiltered) onSelectFiltered(next, { added, removed });
  };

  const handleSelectAll = () => {
    if (disabled || openIsLevelOne) return;
    const allSel = visibleValues.length > 0 && visibleValues.every(v => openSelectedVals.has(v));
    const next = filterSteps.map(s => ({ ...s, values: new Set(s.values) }));
    const idx = next.findIndex(s => s.key === openKey);
    const added = [];
    const removed = [];
    if (allSel) {
      if (idx !== -1) {
        visibleValues.forEach(v => {
          if (next[idx].values.has(v)) {
            next[idx].values.delete(v);
            removed.push({ key: openKey, value: v });
          }
        });
        if (!next[idx].values.size) next.splice(idx, next.length - idx);
      }
    } else {
      if (idx === -1) {
        next.push({ key: openKey, values: new Set(visibleValues) });
        visibleValues.forEach(v => added.push({ key: openKey, value: v }));
      } else {
        visibleValues.forEach(v => {
          if (!next[idx].values.has(v)) {
            next[idx].values.add(v);
            added.push({ key: openKey, value: v });
          }
        });
      }
    }
    onFilterStepsChange(next);
    if (onSelectFiltered) onSelectFiltered(next, { added, removed });
  };

  const removeStep = (key) => {
    if (disabled) return;
    const idx = filterSteps.findIndex(s => s.key === key);
    if (idx >= 0) {
      const removedSteps = filterSteps.slice(idx);
      const newSteps = filterSteps.slice(0, idx);
      onFilterStepsChange(newSteps);
      setOpenKey(null);
      if (onSelectFiltered) {
        const removed = [];
        removedSteps.forEach(s => {
          s.values.forEach(v => {
            removed.push({ key: s.key, value: v });
          });
        });
        onSelectFiltered(newSteps, { added: [], removed });
      }
    }
  };

  const handleOpenKey = (key) => {
    if (disabled) return;
    setOpenKey(prev => prev === key ? null : key);
  };  const allVisibleSelected = visibleValues.length > 0 && visibleValues.every(v => openSelectedVals.has(v));

  return (
    <div ref={ref} style={{ position: "relative", display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px" }}>Filter:</span>

        {activeSteps.map((step, i) => {
          const isOpen = openKey === step.key;
          const matchCount = allQuestions.filter(q => questionMatchesSteps(q, filterSteps.slice(0, i + 1))).length;

          return (
            <span key={step.key} style={{ display: "flex", alignItems: "center", gap: 6, position: "relative" }}>
              {i > 0 && <span style={{ color: "#bae6fd", fontSize: 15, lineHeight: 1 }}>›</span>}
              <button
                onClick={() => handleOpenKey(step.key)}
                style={{
                  display: "flex", alignItems: "center", gap: 5, padding: "5px 8px 5px 10px", borderRadius: 7,
                  border: `1.5px solid ${isOpen ? "#3b82f6" : "#93c5fd"}`,
                  background: isOpen ? "#eff6ff" : "#dbeafe",
                  fontSize: 11, fontWeight: 600, cursor: disabled ? "default" : "pointer",
                  color: "#1d4ed8", transition: "all 0.15s"
                }}
              >
                <span style={{ color: "#3b82f6", fontWeight: 700 }}>{formatTagKey(step.key)}:</span>
                <span style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {Array.from(step.values).join(", ")}
                </span>
                <span style={{ fontSize: 9, fontWeight: 800, padding: "1px 5px", borderRadius: 8, background: "#bfdbfe", color: "#1e40af", marginLeft: 2 }}>{matchCount}</span>
                <span style={{ fontSize: 9, color: "#93c5fd" }}>▾</span>
                {!disabled && (
                  <span
                    onClick={e => { e.stopPropagation(); removeStep(step.key); }}
                    style={{ fontSize: 13, lineHeight: 1, color: "#93c5fd", fontWeight: 900, marginLeft: 1, padding: "0 1px", transition: "color 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
                    onMouseLeave={e => e.currentTarget.style.color = "#93c5fd"}
                  >×</span>
                )}
              </button>
            </span>
          );
        })}

        {activeSteps.length > 0 && nextKeys.length > 0 && (
          <span style={{ color: "#e2e8f0", fontSize: 15, lineHeight: 1 }}>›</span>
        )}

        {nextKeys.map(key => {
          const isOpen = openKey === key;
          return (
            <button key={key} onClick={() => handleOpenKey(key)} style={{
              padding: "5px 12px", borderRadius: 7,
              border: `1.5px solid ${isOpen ? "#93c5fd" : "#e2e8f0"}`,
              background: isOpen ? "#eff6ff" : "#fff",
              fontSize: 11, fontWeight: 600, cursor: disabled ? "default" : "pointer",
              color: isOpen ? "#2563eb" : "#64748b", transition: "all 0.15s"
            }}>
              {formatTagKey(key)} ▾
            </button>
          );
        })}

        {activeSteps.length > 0 && !disabled && (
          <button onClick={() => {
            const removed = [];
            filterSteps.forEach(s => {
              s.values.forEach(v => {
                removed.push({ key: s.key, value: v });
              });
            });
            onFilterStepsChange([]);
            setOpenKey(null);
            if (onSelectFiltered) onSelectFiltered([], { added: [], removed });
          }}
            style={{ fontSize: 10, color: "#ef4444", border: "none", background: "none", cursor: "pointer", fontWeight: 700, marginLeft: 4 }}>
            ✕ Clear
          </button>
        )}
      </div>

      {children}

      {openKey && openValues.length > 0 && !disabled && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 999,
          background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
          boxShadow: "0 8px 24px rgba(0,0,0,0.10)", overflow: "hidden",
          width: 300, maxHeight: 360, display: "flex", flexDirection: "column",
        }}>
          <div style={{ padding: "7px 12px", background: "#f8fafc", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
            <span style={{ fontSize: 10, fontWeight: 800, color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>{formatTagKey(openKey)}</span>
            <span style={{ fontSize: 9, color: "#94a3b8", fontWeight: 600 }}>{openIsLevelOne ? "select one" : "multi-select"}</span>
          </div>

          {!openIsLevelOne && (
            <div style={{ padding: "6px 10px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#94a3b8", pointerEvents: "none" }}>⌕</span>
                <input
                  value={dropdownSearch}
                  onChange={e => setDropdownSearch(e.target.value)}
                  placeholder={`Search ${formatTagKey(openKey)}…`}
                  style={{
                    width: "100%", fontSize: 11, border: "1px solid #e2e8f0", borderRadius: 6,
                    padding: "5px 8px 5px 24px", background: "#fff", color: "#1e293b", outline: "none"
                  }}
                />
                {dropdownSearch && (
                  <span onClick={() => setDropdownSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: "#94a3b8", cursor: "pointer" }}>×</span>
                )}
              </div>
            </div>
          )}

          {!openIsLevelOne && (
            <div style={{ padding: "5px 12px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, background: "#fafafa" }}>
              <span style={{ fontSize: 11, color: "#64748b" }}>
                {openSelectedVals.size} of {openValues.length} selected {dropdownSearch && visibleValues.length !== openValues.length && `(${visibleValues.length} shown)`}
              </span>
              <button onClick={handleSelectAll} style={{ fontSize: 11, fontWeight: 600, color: allVisibleSelected ? "#dc2626" : "#0369a1", background: "none", border: "none", cursor: "pointer", padding: "2px 0" }}>
                {allVisibleSelected ? "Deselect all" : "Select all"}
              </button>
            </div>
          )}

          <div style={{ overflowY: "auto", flex: 1 }}>
            {visibleValues.length === 0 ? (
              <div style={{ padding: "20px 16px", textAlign: "center", fontSize: 11, color: "#94a3b8" }}>No options match your search.</div>
            ) : (
              visibleValues.map(val => {
                const { total, selected, saved } = getValueCounts(openKey, val);
                const isChecked = openSelectedVals.has(val);
                const isAllSelected = disableAllSelected && total > 0 && selected === total && saved === total;
                const itemDisabled = disabled || isAllSelected;

                return (
                  <div key={val} onClick={() => { if (!itemDisabled) toggleValue(openKey, val); }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 12px",
                      cursor: itemDisabled ? "not-allowed" : "pointer",
                      background: isChecked ? "#f0f9ff" : "transparent",
                      borderBottom: "1px solid #f8fafc",
                      transition: "background 0.1s",
                      opacity: itemDisabled ? 0.6 : 1
                    }}
                  >
                    {openIsLevelOne ? (
                      <span style={{
                        width: 17, height: 17, borderRadius: "50%", flexShrink: 0,
                        border: `1.5px solid ${isChecked ? (itemDisabled ? "#94a3b8" : "#3f88a5") : "#cbd5e1"}`,
                        background: isChecked ? (itemDisabled ? "#94a3b8" : "#3f88a5") : "#fff",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        transition: "all 0.15s", boxShadow: isChecked && !itemDisabled ? "0 1px 3px #3f88a544" : "none"
                      }}>
                        {isChecked && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#fff", display: "block" }} />}
                      </span>
                    ) : (
                      <Checkbox checked={isChecked} onChange={() => { if (!itemDisabled) toggleValue(openKey, val); }} disabled={itemDisabled} />
                    )}
                    <span style={{ flex: 1, fontSize: 12, color: "#374151", fontWeight: isChecked ? 600 : 400 }}>{val}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 700, background: isChecked ? "#dbeafe" : "#f1f5f9",
                      color: isChecked ? "#1d4ed8" : "#64748b", borderRadius: 10, padding: "2px 8px",
                      border: `1.5px solid ${isChecked ? "#bfdbfe" : "#e2e8f0"}`, whiteSpace: "nowrap"
                    }}>
                      {openIsLevelOne ? total : `${selected}/${total}`}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
